import { useCallback, useRef, useState } from 'react';
import { IntersectionObserverProps } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';

import { usePrevious, useUnmount } from 'hooks';
import {
    pinStream,
    removeLastPlayedStreamMatchId,
    unpinStream,
} from '../../actions';
import { PlayerType, ViewerMode } from '../../constants';
import {
    selectIsFullScreen,
    selectLastPlayedStreamMatchId,
    selectPinnedStreamMatchId,
    selectViewerMode,
} from '../../selectors';
import { IPlayer } from '../../types';
import { getPlayerType } from '../../utils';

const UNPIN_DELAY = 100;

type HandleIntersection = IntersectionObserverProps['onChange'];

interface Input {
    matchId: string;
    streamUrl: string | null;
    player: IPlayer | null;
    onStreamUnpin: () => void;
    type: 'mobile' | 'desktop';
}

interface Output {
    handleIntersection: HandleIntersection;
    handleStreamUnpin: () => void;
    isPinned: boolean;
}

export default function usePinningWrapper({
    matchId,
    streamUrl,
    player,
    onStreamUnpin,
    type,
}: Input): Output {
    const timerIdRef = useRef<number | undefined>();
    const [isPinned, setIsPined] = useState<boolean>(false);
    const [isInView, setIsInView] = useState<boolean>(false);

    const isPreviouslyInView = usePrevious(isInView);

    const dispatch = useDispatch();
    const isFullScreen = useSelector(selectIsFullScreen);
    const lastPlayedStreamMatchId = useSelector(selectLastPlayedStreamMatchId);
    const pinnedStreamMatchId = useSelector(selectPinnedStreamMatchId);
    const viewerMode = useSelector(selectViewerMode);

    useUnmount(() => {
        if (!isPinned || pinnedStreamMatchId) return;

        player?.pause();

        dispatch(pinStream(matchId));
        dispatch(removeLastPlayedStreamMatchId());

        clearTimeout(timerIdRef.current);
    });

    const handleIntersection: HandleIntersection = (isRefInView, entry) => {
        if (!streamUrl) return;

        if (type === 'desktop') {
            setIsInView(isRefInView);

            if (isPreviouslyInView === isRefInView) return;
        }

        const { intersectionRatio } = entry;

        let shouldPin = !isPinned && !isRefInView;
        let shouldUnpin = isPinned && isRefInView;

        if (type === 'mobile') {
            shouldPin =
                !isPinned &&
                isRefInView &&
                intersectionRatio < 1 &&
                entry.boundingClientRect.top < 0; // Pin the entry only when it is scrolled down

            shouldUnpin = isPinned && intersectionRatio === 1;
        }

        if (shouldUnpin) {
            timerIdRef.current = window.setTimeout(() => {
                setIsPined(false);
            }, UNPIN_DELAY);

            return;
        }

        const playerType = getPlayerType(streamUrl);

        const isPinAvailable =
            !isFullScreen &&
            viewerMode === ViewerMode.STREAM &&
            (lastPlayedStreamMatchId === matchId ||
                ![PlayerType.TWITCH, PlayerType.YOUTUBE].includes(playerType));

        if (isPinAvailable && shouldPin) {
            setIsPined(true);

            if (pinnedStreamMatchId) {
                unpinStream();
            }
        }
    };

    const handleStreamUnpin = useCallback(() => {
        if (!streamUrl) {
            return;
        }

        onStreamUnpin();

        setIsPined(false);
    }, [onStreamUnpin, streamUrl]);

    return {
        handleIntersection,
        handleStreamUnpin,
        isPinned,
    };
}
